import express, { Express, Request, Response, NextFunction } from 'express'
import cors from 'cors'
import { AuthCore } from '../core'
import { RateLimit } from '../types'
import { createApiRoutes } from './api-routes'
import { createOAuthRoutes } from '../frameworks/oauth-routes'
import { setupErrorHandling, asyncHandler } from '../frameworks/error-handler'
import { createRateLimitMiddleware, createPublicRateLimitMiddleware, createPerPrincipalRateLimitMiddleware } from '../frameworks/rate-limit-middleware'
import { RateLimiter, generateRateLimitKey } from '../frameworks/rate-limiter'
import { ValidationError } from '../errors'

/**
 * HTTP API Service for polyglot authentication
 */
export class HttpAuthService {
  private app: Express
  private auth: AuthCore
  private port: number
  private rateLimiter: RateLimiter | null = null
  private globalRateLimit: any = null

  constructor(auth: AuthCore, port: number = 3001) {
    this.auth = auth
    this.port = port
    this.app = express()

    // Try to setup legacy global rate limiter if express-rate-limit is available
    try {
      const rateLimit = require('express-rate-limit')
      this.globalRateLimit = rateLimit({
        windowMs: 15 * 60 * 1000, // 15 minutes
        max: 100, // limit each IP to 100 requests per windowMs
        message: 'Too many requests from this IP',
        skip: (req: Request) => {
          // Skip rate limiting for health check and root endpoint
          return req.path === '/health' || req.path === '/'
        }
      })
    } catch (e) {
      // express-rate-limit not installed, global rate limiting disabled
    }

    this.setupMiddleware()
    this.setupRoutes()
    this.setupErrorHandling()
  }

  /**
   * Setup Express middleware
   */
  private setupMiddleware(): void {
    // Security middleware (optional)
    try {
      const helmet = require('helmet')
      this.app.use(helmet())
    } catch (e) {
      // helmet not installed, security middleware disabled
    }

    // CORS
    this.app.use(
      cors({
        origin: process.env.CORS_ORIGIN || '*',
        credentials: true
      })
    )

    // Body parsing
    this.app.use(express.json({ limit: '10mb' }))
    this.app.use(express.urlencoded({ limit: '10mb', extended: true }))

    // Request logging middleware
    this.app.use((req: Request, res: Response, next: NextFunction) => {
      const start = Date.now()

      res.on('finish', () => {
        const duration = Date.now() - start
        console.log(`[${new Date().toISOString()}] ${req.method} ${req.path} ${res.statusCode} ${duration}ms`)
      })

      next()
    })

    // IP-based rate limiting (legacy, as fallback)
    this.app.use(this.globalRateLimit)

    // API key middleware for protected routes
    this.app.use(this.apiKeyMiddleware.bind(this))

    // Setup per-principal rate limiting if cache is available
    if (this.auth['cache']) {
      this.rateLimiter = new RateLimiter(this.auth['cache'], true)
      this.setupPrincipalRateLimiting()
    }
  }

  /**
   * Setup per-principal rate limiting middleware
   */
  private setupPrincipalRateLimiting(): void {
    if (!this.rateLimiter) {
      return
    }

    // Define rate limits by plan
    const rateLimitsByPlan: Record<string, RateLimit> = {
      free: { limit: 1000, periodSec: 3600 }, // 1000 requests per hour
      basic: { limit: 10000, periodSec: 3600 }, // 10000 requests per hour
      pro: { limit: 100000, periodSec: 3600 }, // 100000 requests per hour
      enterprise: { limit: 1000000, periodSec: 3600 } // 1M requests per hour
    }

    // Add per-principal rate limit middleware
    const rateLimitMiddleware = createRateLimitMiddleware({
      rateLimiter: this.rateLimiter,
      getRateLimitForRequest: (req) => {
        // Get rate limit from API key verification
        const apiKey = (req as any).apiKey
        if (apiKey?.rateLimit) {
          return apiKey.rateLimit
        }

        // Get rate limit from plan if authenticated
        const apiKeyVerification = (req as any).apiKey
        if (apiKeyVerification?.plan) {
          return rateLimitsByPlan[apiKeyVerification.plan] || rateLimitsByPlan.free
        }

        // Skip rate limiting for unauthenticated requests
        return null
      },
      onBefore: async (req, key) => {
        // Log rate limit checks for authenticated requests
        if ((req as any).apiKey?.principalId) {
          console.log(`[RateLimit] Checking limit for principal: ${key}`)
        }
      },
      onLimitExceeded: async (req, key, error) => {
        console.log(`[RateLimit] Limit exceeded for principal: ${key}`, {
          resetAt: error.resetAt,
          retryAfter: error.retryAfter
        })
      }
    })

    this.app.use(rateLimitMiddleware)
  }

  /**
   * API key validation middleware
   */
  private async apiKeyMiddleware(
    req: Request,
    res: Response,
    next: NextFunction
  ) {
    // Extract API key from header or query
    const apiKey = req.headers['x-api-key'] || req.query.api_key

    if (apiKey && typeof apiKey === 'string') {
      try {
        const result = await this.auth.verifyApiKey(apiKey)
        if (result.valid) {
          ;(req as any).apiKey = result
        }
      } catch (error) {
        // Continue without auth if key verification fails
      }
    }

    next()
  }

  /**
   * Setup routes
   */
  private setupRoutes(): void {
    // API routes
    this.app.use(createApiRoutes(this.auth))

    // OAuth routes
    this.app.use(createOAuthRoutes(this.auth))

    // JWKS endpoint
    this.app.get(
      '/.well-known/jwks.json',
      asyncHandler(async (req: Request, res: Response) => {
        // TODO: Implement JWKS endpoint with public keys
        res.json({
          keys: []
        })
      })
    )

    // Root endpoint
    this.app.get(
      '/',
      asyncHandler(async (req: Request, res: Response) => {
        res.json({
          name: '@kitium/auth',
          version: '1.0.0',
          docs: '/info',
          health: '/health'
        })
      })
    )
  }

  /**
   * Setup error handling
   */
  private setupErrorHandling(): void {
    setupErrorHandling(this.app)
  }

  /**
   * Start the HTTP service
   */
  async start(): Promise<void> {
    return new Promise((resolve) => {
      this.app.listen(this.port, () => {
        console.log(`\n✅ Auth Service running on http://localhost:${this.port}\n`)
        console.log(`📚 API Documentation: http://localhost:${this.port}/info`)
        console.log(`🏥 Health Check: http://localhost:${this.port}/health\n`)
        resolve()
      })
    })
  }

  /**
   * Stop the HTTP service
   */
  async stop(): Promise<void> {
    // TODO: Implement graceful shutdown
  }

  /**
   * Get rate limiter instance
   */
  getRateLimiter(): RateLimiter | null {
    return this.rateLimiter
  }

  /**
   * Check current rate limit status for a principal
   */
  async getRateLimitStatus(principalId: string) {
    if (!this.rateLimiter) {
      return null
    }

    return this.rateLimiter.getStatus(principalId)
  }

  /**
   * Reset rate limit for a principal (admin operation)
   */
  async resetRateLimit(principalId: string): Promise<void> {
    if (!this.rateLimiter) {
      return
    }

    await this.rateLimiter.reset(principalId)
    console.log(`[RateLimit] Reset rate limit for principal: ${principalId}`)
  }

  /**
   * Get Express app for testing
   */
  getApp(): Express {
    return this.app
  }
}

/**
 * Factory function to create and start the HTTP service
 */
export async function startAuthService(
  auth: AuthCore,
  port: number = 3001
): Promise<HttpAuthService> {
  const service = new HttpAuthService(auth, port)
  await service.start()
  return service
}
